import { useState, useEffect } from 'react'
import { auth, db } from '../lib/firebase'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { updateProfile } from 'firebase/auth'
import {
  User,
  Trophy,
  Star,
  TrendingUp,
  Settings,
  Bell,
  Shield,
  Save
} from 'lucide-react'

const Profile = () => {
  const [userData, setUserData] = useState(null)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    favoriteSport: 'NBA',
    favoriteTeams: [],
    notifications: true
  })

  useEffect(() => {
    loadUserProfile()
  }, [])

  const loadUserProfile = async () => {
    if (auth.currentUser) {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        setUserData(data)
        setFormData({
          name: data.name || '',
          favoriteSport: data.settings?.favoriteSport || 'NBA',
          favoriteTeams: data.settings?.favoriteTeams || [],
          notifications: data.settings?.notifications !== false
        })
      }
    }
  }

  const handleSave = async () => {
    try {
      // Update display name in Auth
      await updateProfile(auth.currentUser, {
        displayName: formData.name
      })

      // Update Firestore
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        name: formData.name,
        'settings.favoriteSport': formData.favoriteSport,
        'settings.favoriteTeams': formData.favoriteTeams,
        'settings.notifications': formData.notifications
      })

      setUserData({
        ...userData,
        name: formData.name,
        settings: {
          ...userData.settings,
          favoriteSport: formData.favoriteSport,
          favoriteTeams: formData.favoriteTeams,
          notifications: formData.notifications
        }
      })

      setEditing(false)
    } catch (error) {
      console.error('Error updating profile:', error)
    }
  }

  const achievements = [
    { id: 1, name: 'Data Explorer', description: 'View your first game analysis', icon: '🏆', unlocked: true },
    { id: 2, name: 'Analytics Pro', description: 'View 50 game analyses', icon: '🔥', unlocked: true },
    { id: 3, name: 'NBA Expert', description: 'Master NBA analytics', icon: '🏀', unlocked: false },
    { id: 4, name: 'Stats Guru', description: 'View 100 detailed analyses', icon: '⭐', unlocked: false }
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Profile</h1>

      {/* Profile Card */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {userData?.name?.charAt(0) || 'U'}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{userData?.name || 'User'}</h2>
              <p className="text-gray-400">Member since {userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'Today'}</p>
            </div>
          </div>
          <button
            onClick={() => editing ? handleSave() : setEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            {editing ? <Save className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
            {editing ? 'Save Changes' : 'Edit Profile'}
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <p className="text-gray-400 text-sm">Level</p>
            <p className="text-xl font-bold text-white">{userData?.level || 1}</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <p className="text-gray-400 text-sm">Total XP</p>
            <p className="text-xl font-bold text-white">{userData?.xp || 0}</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <p className="text-gray-400 text-sm">Login Streak</p>
            <p className="text-xl font-bold text-white">{userData?.loginStreak || 0}</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <p className="text-gray-400 text-sm">Games Analyzed</p>
            <p className="text-xl font-bold text-white">{userData?.gamesViewed || 0}</p>
          </div>
        </div>

        {/* Settings Form */}
        {editing && (
          <div className="space-y-4 border-t border-dark-border pt-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Favorite Sport
              </label>
              <select
                value={formData.favoriteSport}
                onChange={(e) => setFormData({...formData, favoriteSport: e.target.value})}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="NBA">NBA</option>
                <option value="NFL">NFL</option>
                <option value="MLB">MLB</option>
                <option value="Soccer">Soccer</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifications}
                  onChange={(e) => setFormData({...formData, notifications: e.target.checked})}
                  className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-white">Enable push notifications</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Achievements */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Achievements</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`flex items-center gap-4 p-4 rounded-lg ${
                achievement.unlocked
                  ? 'bg-gradient-to-r from-primary-500/20 to-purple-500/20 border border-primary-500/30'
                  : 'bg-gray-800/30 border border-gray-700 opacity-50'
              }`}
            >
              <div className="text-3xl">{achievement.icon}</div>
              <div className="flex-1">
                <h3 className="font-medium text-white">{achievement.name}</h3>
                <p className="text-sm text-gray-400">{achievement.description}</p>
              </div>
              {achievement.unlocked && (
                <Star className="h-5 w-5 text-yellow-500" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Level Progress */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Level Progress</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Level {userData?.level || 1}</span>
            <span className="text-gray-400">Level {(userData?.level || 1) + 1}</span>
          </div>
          <div className="bg-gray-700 rounded-full h-4">
            <div
              className="bg-gradient-to-r from-primary-500 to-purple-500 h-4 rounded-full transition-all"
              style={{width: '35%'}}
            ></div>
          </div>
          <p className="text-center text-sm text-gray-400">
            {userData?.xp || 0} / {((userData?.level || 1) + 1) * 100} XP to next level
          </p>
        </div>
      </div>
    </div>
  )
}

export default Profile